import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, Users, Shield, Trash2, Edit2, Plus, Lock } from 'lucide-react';
import { toast } from 'sonner';

interface User {
  id: string;
  userId: string;
  password: string;
  nama: string;
  role: 'Admin' | 'Pentadbir' | 'Staff';
  email?: string;
  aktif: boolean;
  tarikhBuat: string;
}

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([
    { 
      id: '1', 
      userId: 'admin', 
      password: 'admin123', 
      nama: 'Administrator', 
      role: 'Admin',
      email: 'admin@sapngecat.com',
      aktif: true,
      tarikhBuat: '2026-05-25'
    },
    { 
      id: '2', 
      userId: 'pentadbir', 
      password: 'pentadbir123', 
      nama: 'Pentadbir SAP', 
      role: 'Pentadbir',
      email: 'pentadbir@sapngecat.com',
      aktif: true,
      tarikhBuat: '2026-05-25'
    },
    { 
      id: '3', 
      userId: 'staff', 
      password: 'staff123', 
      nama: 'Staff', 
      role: 'Staff',
      email: 'staff@sapngecat.com',
      aktif: true,
      tarikhBuat: '2026-05-25'
    },
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({});

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({
      userId: '',
      password: '',
      nama: '',
      role: 'Staff',
      email: '',
      aktif: true,
      tarikhBuat: new Date().toISOString().split('T')[0]
    });
    setShowAddForm(true);
  };

  const handleEdit = (user: User) => {
    setEditingId(user.id);
    setFormData(user);
    setShowAddForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.userId || !formData.password || !formData.nama || !formData.role) {
      toast.error('Semua field wajib diisi!');
      return;
    }

    if (editingId) {
      const updated = users.map(u => u.id === editingId ? { ...formData as User, id: editingId } : u);
      setUsers(updated);
      toast.success('User berhasil diupdate');
    } else {
      const newUser: User = {
        id: Date.now().toString(),
        userId: formData.userId!,
        password: formData.password!,
        nama: formData.nama!,
        role: formData.role as any,
        email: formData.email,
        aktif: true,
        tarikhBuat: new Date().toISOString().split('T')[0]
      };
      setUsers([...users, newUser]);
      toast.success('User berhasil ditambahkan');
    }

    setShowAddForm(false);
  };

  const handleDelete = (id: string) => {
    if (users.filter(u => u.role === 'Admin').length === 1 && users.find(u => u.id === id)?.role === 'Admin') {
      toast.error('Minimal harus ada 1 Admin!');
      return;
    }

    const filtered = users.filter(u => u.id !== id);
    setUsers(filtered);
    toast.success('User berhasil dihapus');
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'bg-red-100 text-red-700';
      case 'Pentadbir': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Admin': return <Shield size={16} />;
      case 'Pentadbir': return <Lock size={16} />;
      default: return <Users size={16} />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pengurusan Pengguna</h1>
          <p className="text-slate-600 mt-1">Kelola akun pengguna sistem SAP Ngecat</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
          <Plus size={18} />
          <span className="font-semibold">Pengguna Baru</span>
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertCircle className="text-blue-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-blue-900">Informasi Penting</p>
              <p className="text-sm text-blue-800 mt-1">
                Jangan lupa ubah password default sebelum go-live. Setiap user harus memiliki password yang kuat dan unik.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Jumlah Pengguna</p>
              <p className="text-3xl font-bold text-slate-900">{users.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Admin</p>
              <p className="text-3xl font-bold text-red-600">{users.filter(u => u.role === 'Admin').length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Pentadbir</p>
              <p className="text-3xl font-bold text-blue-600">{users.filter(u => u.role === 'Pentadbir').length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Aktif</p>
              <p className="text-3xl font-bold text-green-600">{users.filter(u => u.aktif).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daftar Pengguna</CardTitle>
          <CardDescription>Kelola semua akun pengguna sistem</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">User ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Nama</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Role</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Tarikh Buat</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">{user.userId}</td>
                    <td className="py-3 px-4 text-slate-600">{user.nama}</td>
                    <td className="py-3 px-4 text-slate-600">{user.email || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                        {getRoleIcon(user.role)}
                        {user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        user.aktif ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {user.aktif ? 'Aktif' : 'Tidak Aktif'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{user.tarikhBuat}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-700 text-sm gap-1 flex items-center"
                        >
                          <Edit2 size={14} /> Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(user.id)}
                          className="text-red-600 hover:text-red-700 text-sm gap-1 flex items-center"
                        >
                          <Trash2 size={14} /> Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{editingId ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">User ID *</label>
                  <Input
                    type="text"
                    value={formData.userId || ''}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    placeholder="Contoh: khairul"
                    className="border-slate-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                  <Input
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Masukkan password yang kuat"
                    className="border-slate-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nama Lengkap *</label>
                  <Input
                    type="text"
                    value={formData.nama || ''}
                    onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                    placeholder="Contoh: Khairul Azmi"
                    className="border-slate-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="khairul@sapngecat.com"
                    className="border-slate-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                  <select
                    value={formData.role || 'Staff'}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="Admin">Admin (Full Access)</option>
                    <option value="Pentadbir">Pentadbir (Admin Access)</option>
                    <option value="Staff">Staff (Standard User)</option>
                  </select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
                    {editingId ? 'Update' : 'Tambah'} User
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowAddForm(false)}
                  >
                    Batal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Best Practices */}
      <Card>
        <CardHeader>
          <CardTitle>Best Practices untuk Pengurusan User</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Ubah semua password default sebelum go-live</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Gunakan password yang kuat (minimal 8 karakter, mix letters & numbers)</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Berikan role yang sesuai dengan tanggung jawab user</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Minimal satu user dengan role Admin</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Hapus user yang tidak aktif lagi</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red-600 font-bold flex-shrink-0">✗</span>
              <span className="text-sm text-slate-700">Jangan share password dengan colleague lain</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red-600 font-bold flex-shrink-0">✗</span>
              <span className="text-sm text-slate-700">Jangan gunakan password yang mudah (123456, qwerty, etc)</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
